import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Button,
  Avatar,
  Typography,
  Row,
  Col,
  Form,
  Input,
  Tag,
  Progress,
  message,
  Divider,
  Space
} from 'antd';
import {
  UserOutlined,
  CreditCardOutlined,
  SafetyCertificateOutlined,
  EditOutlined,
  SaveOutlined,
  MailOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  CrownFilled,
  StarFilled,
  ThunderboltOutlined,
  GiftOutlined,
  RocketOutlined
} from '@ant-design/icons';
import { AppTheme } from '../../theme/themes';
import PlanSelectionView from './PlanSelectionView';
import { APP_CONFIG } from '../../config/generated';
import { logger } from '../../utils/logger';
import { createBillingPortalSession } from '../../services/billingService';

import BadgeShowcase from './BadgeShowcase';
import { useUsageContext } from '../../contexts/UsageContext';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

interface UserProfileProps {
  user: any;
  theme: AppTheme;
}

const UserProfile: React.FC<UserProfileProps> = ({ user, theme }) => {
  const { t, i18n } = useTranslation();
  // Local state for the view mode
  const [currentView, setCurrentView] = useState<'profile' | 'plans'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const { usageStatus } = useUsageContext();
  
  // Mock user profile data combined with actual auth data
  const [profileData, setProfileData] = useState({
    firstName: "Trader",
    lastName: "One",
    email: user?.attributes?.email || "user@example.com",
    memberSince: "December 2024",
    plan: "Starter", // Default plan (will be overridden by backend entitlements)
    planUsage: 0, // Daily used tokens
    planLimit: 5, // Daily AI limit for Starter (will be adjusted by plan)
    avatar: "T1",
    renewText: '' as string | null
  });

  // Initialize data from props
  useEffect(() => {
    if (user) {
      // Extract username or email parts for first/last name simulation
      // Or use custom attributes if they exist
      const customName = user.attributes?.given_name || '';
      const customFamilyName = user.attributes?.family_name || '';
      
      // Fallback to email if no name set
      let firstName = customName;
      let lastName = customFamilyName;
      
      if (!firstName) {
        const emailParts = (user.attributes?.email || '').split('@')[0].split('.');
        firstName = emailParts[0] || 'Trader';
        lastName = emailParts[1] || 'One';
      }
      
      const avatarText = (firstName.charAt(0) + (lastName ? lastName.charAt(0) : '')).toUpperCase();
      const displayEmail = user.attributes?.email || profileData.email;

      setProfileData(prev => ({
        ...prev,
        firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1),
        lastName: lastName ? lastName.charAt(0).toUpperCase() + lastName.slice(1) : prev.lastName,
        email: displayEmail,
        avatar: avatarText
      }));
      
      form.setFieldsValue({
        firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1),
        lastName: lastName ? lastName.charAt(0).toUpperCase() + lastName.slice(1) : '',
        email: displayEmail
      });
    }
  }, [user, form]);

  // Fetch billing entitlements from backend (DynamoDB-based)
  useEffect(() => {
    const fetchEntitlements = async () => {
      try {
        if (!user) return;

        const idToken = localStorage.getItem('cognito_id_token');
        if (!idToken) {
          logger.warn('No ID token found when fetching entitlements');
          return;
        }

        const mode = (APP_CONFIG as any).mode || 'dev';
        const envConfig = (APP_CONFIG as any).ENV_CONFIGS?.[mode] || APP_CONFIG.ENV_CONFIGS.dev;
        const backendUrl = (envConfig?.API || 'http://localhost:5050').replace(/\/$/, '');
        const { getApiHeadersWithoutContentType } = await import('../../utils/apiHeaders');

        const resp = await fetch(`${backendUrl}/api/billing/entitlements`, {
          method: 'GET',
          headers: getApiHeadersWithoutContentType()
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          logger.error('Failed to load entitlements', data);
          return;
        }

        const entitlements = data.entitlements || {};
        const planCode = entitlements.plan || 'free';
        const dailyUsed = entitlements.daily_used ?? 0;
        const dailyLimitFromBackend = entitlements.daily_limit;
        const firstNameFromProfile = entitlements.first_name;
        const lastNameFromProfile = entitlements.last_name;
        const rawCurrentPeriodEnd = entitlements.current_period_end;
        const cancelAtPeriodEnd = entitlements.cancel_at_period_end;

        // Backend may return current_period_end as a string or number; normalize to number
        const currentPeriodEnd =
          typeof rawCurrentPeriodEnd === 'number'
            ? rawCurrentPeriodEnd
            : typeof rawCurrentPeriodEnd === 'string'
              ? Number(rawCurrentPeriodEnd)
              : null;

        // Map backend plan code to UI plan name
        let uiPlanName = 'Starter';
        if (planCode === 'pro') {
          uiPlanName = 'Pro Trader';
        } else if (planCode === 'premium' || planCode === 'enterprise') {
          uiPlanName = 'Enterprise';
        }

        // Prefer backend daily_limit if present; otherwise fall back to simple defaults
        let uiPlanLimit: number;
        if (typeof dailyLimitFromBackend === 'number' && dailyLimitFromBackend > 0) {
          uiPlanLimit = dailyLimitFromBackend;
        } else if (planCode === 'pro') {
          uiPlanLimit = 50;
        } else if (planCode === 'premium' || planCode === 'enterprise') {
          uiPlanLimit = 9999;
        } else {
          uiPlanLimit = 5;
        }

        // Compute renew / end text from current_period_end + cancel_at_period_end.
        // For free plan, we intentionally do not show any renewal text.
        let renewText: string | null = null;
        if (planCode !== 'free' && typeof currentPeriodEnd === 'number' && currentPeriodEnd > 0) {
          const date = new Date(currentPeriodEnd * 1000);
          const locale = i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US';
          const formatted = date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
          if (cancelAtPeriodEnd) {
            renewText = t('profile.plan.endsOn', { date: formatted });
          } else {
            renewText = t('profile.plan.renewsOn', { date: formatted });
          }
        }

        setProfileData(prev => ({
          ...prev,
          ...(firstNameFromProfile ? { firstName: firstNameFromProfile } : {}),
          ...(lastNameFromProfile ? { lastName: lastNameFromProfile } : {}),
          plan: uiPlanName,
          planUsage: typeof dailyUsed === 'number' ? dailyUsed : prev.planUsage,
          planLimit: uiPlanLimit,
          renewText: renewText !== null ? renewText : prev.renewText
        }));

        if (firstNameFromProfile || lastNameFromProfile) {
          form.setFieldsValue({
            firstName: firstNameFromProfile || form.getFieldValue('firstName'),
            lastName: lastNameFromProfile || form.getFieldValue('lastName'),
          });
        }
      } catch (err) {
        logger.error('Unexpected error while fetching entitlements', err);
      }
    };

    fetchEntitlements();
  }, [user]);


  const handleEditToggle = () => {
    if (isEditing) {
      // Cancelled
      form.resetFields();
    }
    setIsEditing(!isEditing);
  };

  const handleSaveProfile = async () => {
    try {
      const values = await form.validateFields();

      const idToken = localStorage.getItem('cognito_id_token');
      if (!idToken) {
        message.error(t('profile.messages.mustLoginUpdateProfile'));
        return;
      }

      const mode = (APP_CONFIG as any).mode || 'dev';
      const envConfig = (APP_CONFIG as any).ENV_CONFIGS?.[mode] || APP_CONFIG.ENV_CONFIGS.dev;
      const backendUrl = (envConfig?.API || 'http://localhost:5050').replace(/\/$/, '');
      const { getApiHeaders } = await import('../../utils/apiHeaders');

      const resp = await fetch(`${backendUrl}/api/user/profile`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          first_name: values.firstName,
          last_name: values.lastName,
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        message.error(data.error || t('profile.messages.updateFailed'));
        return;
      }

      setProfileData(prev => ({
        ...prev,
        firstName: values.firstName,
        lastName: values.lastName,
        avatar: (values.firstName.charAt(0) + (values.lastName ? values.lastName.charAt(0) : '')).toUpperCase(),
      }));
      setIsEditing(false);
      message.success(t('profile.messages.updateSuccess'));
    } catch (error) {
      // Validation failed
      console.error('Profile update validation failed:', error);
    }
  };

  const handlePlanChange = (newPlanName: string) => {
    setProfileData(prev => ({ ...prev, plan: newPlanName, planLimit: newPlanName === 'Pro Trader' ? 100 : newPlanName === 'Starter' ? 5 : 9999 }));
    setCurrentView('profile');
    message.success(t('profile.messages.planUpgraded', { plan: newPlanName }));
  };

  const handleManageSubscription = async () => {
    if (!user) {
      message.error(t('profile.messages.mustLoginManageSub'));
      return;
    }
    const url = await createBillingPortalSession(user);
    if (!url) {
      message.error(t('profile.messages.billingPortalFailed'));
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (currentView === 'plans') {
    return (
      <PlanSelectionView 
        user={user}
        currentPlan={profileData.plan}
        onSelectPlan={handlePlanChange}
        onManageSubscription={handleManageSubscription}
        onBack={() => setCurrentView('profile')}
        theme={theme}
      />
    );
  }

  // Prefer backend usage status (shared with top-right UsageIndicator);
  // fall back to entitlements-based values if not available.
  const effectiveUsed = typeof usageStatus?.used === 'number' ? usageStatus.used : profileData.planUsage;
  const effectiveLimit =
    typeof usageStatus?.limit === 'number' && usageStatus.limit > 0
      ? usageStatus.limit
      : profileData.planLimit;

  const usagePercent =
    effectiveLimit > 0
      ? Math.min(100, Math.round((effectiveUsed / effectiveLimit) * 100))
      : 0;

  const isPaidPlan = profileData.plan !== 'Starter';
  const isEnterprise = profileData.plan === 'Enterprise';
  const isPro = profileData.plan === 'Pro Trader';

  // Dynamic header style for "Noble" look
  const headerStyle: React.CSSProperties = isEnterprise
    ? { background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' } // Dark Slate
    : isPro
      ? { background: 'linear-gradient(135deg, #4338ca 0%, #312e81 100%)' } // Deep Indigo
      : { background: `linear-gradient(135deg, ${theme.accentGradientFrom}, ${theme.accentGradientTo})` }; // Default

  const iconStyle = { fontSize: 20, color: isEnterprise || isPro ? '#fbbf24' : '#fff' }; // Gold icon for paid

  return (
    <div className="animate-fade-in" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ color: theme.textPrimary, marginBottom: 8 }}>{t('profile.accountSettings')}</Title>
        <Text style={{ color: theme.textSecondary }}>{t('profile.accountSettingsSubtitle')}</Text>
      </div>

      <Row gutter={[24, 24]}>
        
        {/* Left Column: Plan & Status */}
        <Col xs={24} lg={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            
            {/* Plan Card */}
            <Card
              bordered={false}
              style={{ 
                overflow: 'hidden', 
                padding: 0,
                border: isPaidPlan ? `1px solid ${isEnterprise ? '#fbbf24' : theme.accentPrimary}` : `1px solid ${theme.surfaceBorder}`,
                background: theme.surfaceBackground,
                boxShadow: isPaidPlan ? '0 8px 24px rgba(0,0,0,0.12)' : 'none'
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div style={{
                ...headerStyle,
                padding: 24,
                color: '#fff',
                position: 'relative'
              }}>
                {/* Decorative shine for paid plans */}
                {(isPro || isEnterprise) && (
                  <div style={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 100,
                    height: 100,
                    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
                    borderRadius: '50%'
                  }} />
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, position: 'relative', zIndex: 1 }}>
                  <Space align="center" style={{ opacity: 0.95 }}>
                    {isEnterprise ? <CrownFilled style={iconStyle} /> : 
                     isPro ? <StarFilled style={iconStyle} /> :
                     <CreditCardOutlined />}
                    <Text style={{ color: isEnterprise || isPro ? '#fbbf24' : '#fff', fontWeight: isPaidPlan ? 600 : 400 }}>
                      {isEnterprise
                        ? t('profile.badge.eliteStatus')
                        : isPro
                          ? t('profile.badge.proMember')
                          : t('profile.badge.currentPlan')}
                    </Text>
                  </Space>
                  {isPaidPlan && (
                    <Tag 
                      color={isEnterprise ? "#fbbf24" : "rgba(255,255,255,0.2)"} 
                      style={{ 
                        border: isEnterprise ? 'none' : '1px solid rgba(255,255,255,0.4)', 
                        color: isEnterprise ? '#000' : '#fff', 
                        marginRight: 0,
                        fontWeight: isEnterprise ? 'bold' : 500
                      }}
                    >
                      {t('profile.badge.active')}
                    </Tag>
                  )}
                </div>
                <Title level={3} style={{ color: isEnterprise || isPro ? '#fff' : '#fff', margin: '0 0 4px 0', position: 'relative', zIndex: 1 }}>{profileData.plan}</Title>
                {profileData.renewText && (
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, position: 'relative', zIndex: 1 }}>
                    {profileData.renewText}
                  </Text>
                )}
              </div>

              <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Text style={{ color: theme.textSecondary }}>{t('profile.plan.usageTitle')}</Text>
                    <Text strong style={{ color: theme.accentPrimary }}>{usagePercent}%</Text>
                  </div>
                  <Progress 
                    percent={usagePercent} 
                    showInfo={false} 
                    strokeColor={theme.accentPrimary} 
                    trailColor={theme.appBackground}
                  />
                  <div style={{ textAlign: 'right', marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('profile.plan.tokensUsed', { used: effectiveUsed, limit: effectiveLimit })}
                    </Text>
                  </div>
                </div>

                <Button
                  block
                  icon={<RiseOutlined />}
                  onClick={() => setCurrentView('plans')}
                  style={{
                    borderColor: theme.accentPrimary,
                    color: theme.accentPrimary,
                    height: 40,
                    fontWeight: 500
                  }}
                >
                  {isPaidPlan ? t('profile.plan.manage') : t('profile.plan.upgrade')}
                </Button>
                {/* Only show Stripe subscription management for Pro, not for Enterprise */}
                {isPro && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0 }}
                      onClick={handleManageSubscription}
                    >
                      {t('profile.plan.manageSubscription')}
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* Security Box */}
            <Card 
              title={
                <Space>
                  <SafetyCertificateOutlined style={{ color: theme.textSecondary }} />
                  <span style={{ color: theme.textPrimary }}>{t('profile.security.title')}</span>
                </Space>
              }
              bordered={false}
              style={{ background: theme.surfaceBackground, border: `1px solid ${theme.surfaceBorder}` }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 16, borderBottom: `1px solid ${theme.surfaceBorder}` }}>
                <div>
                  <div style={{ color: theme.textPrimary, fontWeight: 500 }}>{t('profile.security.twoFactor')}</div>
                  <div style={{ color: theme.textSecondary, fontSize: 12 }}>{t('profile.security.twoFactorSubtitle')}</div>
                </div>
                <Tag color="success">{t('profile.badge.active')}</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16 }}>
                <div>
                  <div style={{ color: theme.textPrimary, fontWeight: 500 }}>{t('profile.security.password')}</div>
                  <div style={{ color: theme.textSecondary, fontSize: 12 }}>{t('profile.security.passwordSubtitle')}</div>
                </div>
                <Button type="link" size="small" style={{ padding: 0 }}>{t('profile.security.update')}</Button>
              </div>
            </Card>
          </div>
        </Col>

        {/* Right Column: Personal Information */}
        <Col xs={24} lg={16}>
          <Card 
            title={<span style={{ color: theme.textPrimary }}>{t('profile.personalInfo.title')}</span>}
            extra={
              !isEditing ? (
                <Button icon={<EditOutlined />} onClick={handleEditToggle}>{t('profile.personalInfo.edit')}</Button>
              ) : (
                <Space>
                  <Button onClick={handleEditToggle}>{t('profile.personalInfo.cancel')}</Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveProfile}>{t('profile.personalInfo.save')}</Button>
                </Space>
              )
            }
            bordered={false}
            style={{ height: '100%', background: theme.surfaceBackground, border: `1px solid ${theme.surfaceBorder}` }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32 }}>
              <Avatar 
                size={80} 
                style={{ 
                  backgroundColor: theme.accentPrimary, 
                  fontSize: 32, 
                  marginRight: 24,
                  verticalAlign: 'middle'
                }}
              >
                {profileData.avatar}
              </Avatar>
              <div>
                <Title level={4} style={{ color: theme.textPrimary, margin: 0 }}>
                  {profileData.firstName} {profileData.lastName}
                </Title>
                <Text style={{ color: theme.textSecondary }}>{profileData.email}</Text>
              </div>
            </div>

            <Form 
              form={form} 
              layout="vertical" 
              initialValues={{
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                email: profileData.email
              }}
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item label={t('profile.personalInfo.firstName')} name="firstName">
                    <Input disabled={!isEditing} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t('profile.personalInfo.lastName')} name="lastName">
                    <Input disabled={!isEditing} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Divider style={{ margin: '24px 0' }} />

            <Row gutter={24}>
              <Col xs={24}>
                <div style={{ 
                  background: theme.appBackground, 
                  borderRadius: 12, 
                  padding: '16px 20px',
                  marginBottom: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <Text strong style={{ color: theme.textPrimary, display: 'block', fontSize: 14 }}>{t('profile.invite.title')}</Text>
                    <Text style={{ color: theme.textSecondary, fontSize: 12 }}>{t('profile.invite.subtitle')}</Text>
                  </div>
                  <Button type="primary" ghost size="small" icon={<GiftOutlined />}>{t('profile.invite.button')}</Button>
                </div>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24}>
                <BadgeShowcase theme={theme} />
              </Col>
            </Row>
          </Card>
        </Col>

      </Row>
    </div>
  );
};

export default UserProfile;
